import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { emailSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const loginSchema = z.object({
  email: emailSchema,
  // Sign-in only checks the field is non-empty; the strength policy lives on
  // the sign-up form. Enforcing a length here would leak the policy and could
  // block accounts created before it changed.
  password: z.string().min(1, 'Enter your password'),
});
type LoginInput = z.infer<typeof loginSchema>;

function LoginPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  async function onSubmit(values: LoginInput) {
    setStatus('submitting');
    setServerError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[login] signInWithPassword failed', { code: error.code, status: error.status });
      setStatus('error');
      // Distinguish only the one case the user can act on: an unconfirmed
      // email. Everything else stays generic to avoid account enumeration
      // (no "wrong password" vs "no such user").
      const unconfirmed =
        error.code === 'email_not_confirmed' || /email not confirmed/i.test(error.message ?? '');
      setServerError(
        unconfirmed
          ? 'Please confirm your email — check your inbox for the link we sent.'
          : 'Incorrect email or password.',
      );
      return;
    }
    // Password sign-in produces a session synchronously. Let the root index
    // guard read it and redirect to the role-appropriate dashboard.
    await navigate({ to: '/' });
  }

  return (
    <main
      id="main"
      className="grid min-h-dvh grid-cols-1 lg:grid-cols-[1.1fr_1fr]"
      data-testid="login-page"
    >
      {/* Editorial hero — anti-template per web/design-quality.md.
          The marketing-y left panel exists on desktop only; mobile shows
          the form alone with breathing room. */}
      <aside
        aria-hidden="true"
        className="relative hidden overflow-hidden bg-[var(--color-surface-sunken)] lg:block"
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <p className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-lg)] uppercase">
            Pirouette
          </p>
          <div className="max-w-xl">
            <h1 className="leading-[var(--leading-tight)] font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--color-ink)] text-[var(--text-display)]">
              The studio,
              <br />
              <em className="text-[var(--color-accent)] not-italic">in time.</em>
            </h1>
            <p className="mt-6 max-w-md leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
              Tuition, schedules, recital lineups, and ticketing — one quiet system that handles the
              season so you can run the studio.
            </p>
          </div>
          <p className="text-[var(--color-ink-subtle)] text-[var(--text-sm)]">
            Designed for one studio. Built to stay out of the way.
          </p>
        </div>
      </aside>

      <section className="flex items-center justify-center px-6 py-16 lg:px-12">
        <div className="w-full max-w-sm">
          <h2 className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-3xl)]">
            Welcome back
          </h2>
          <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-base)]">
            Sign in with your email and password.
          </p>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
            className="mt-8 space-y-5"
          >
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p
                  id="email-error"
                  role="alert"
                  className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                >
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-[var(--color-accent)] text-[var(--text-sm)] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p
                  id="password-error"
                  role="alert"
                  className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                >
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={!isValid || status === 'submitting'}
              aria-busy={status === 'submitting'}
              className="w-full"
            >
              {status === 'submitting' ? 'Signing in…' : 'Sign in'}
            </Button>

            {serverError && (
              <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
                {serverError}
              </p>
            )}
          </form>

          <p className="mt-8 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            New to Pirouette?{' '}
            <Link to="/signup" className="text-[var(--color-accent)] underline">
              Create an account
            </Link>
          </p>

          <p className="mt-12 text-[var(--color-ink-subtle)] text-[var(--text-sm)]">
            You'll need a join code from your studio to create an account.{' '}
            <Link to="/contact" className="text-[var(--color-accent)] underline">
              Contact Pirouette
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}
